class GiftSubscriptionForm extends HTMLElement {
  constructor() {
    super();
    this.inputs = [];
    this.inputChangeListeners = [];
    this.productFormSubmitListener = null;
    this.initialized = false;
  }

  connectedCallback() {
    // Delay initialization to ensure DOM is fully rendered
    setTimeout(() => {
      this.initializeForm();
    }, 100);
  }

  disconnectedCallback() {
    this.cleanup();
  }

  initializeForm() {
    if (this.initialized) {
      this.cleanup();
    }

    // Discover inputs after DOM is ready
    this.inputs = this.querySelectorAll("input, select, textarea");
    
    if (this.inputs.length === 0) {
      // If still no inputs, wait a bit longer for potential re-renders
      setTimeout(() => {
        this.inputs = this.querySelectorAll("input, select, textarea");
        if (this.inputs.length > 0) {
          this.setupEventListeners();
        }
      }, 200);
    } else {
      this.setupEventListeners();
    }

    this.initialized = true;
  }

  setupEventListeners() {
    this.listenForInputChange();
    this.listenForProductFormSubmit();
  }

  cleanup() {
    // Remove all input event listeners
    this.inputChangeListeners.forEach(({ input, listener }) => {
      input.removeEventListener("change", listener);
    });
    this.inputChangeListeners = [];

    // Remove product form submit listener
    if (this.productFormSubmitListener) {
      const productFormSubmit = document.querySelector('.product-form [type="submit"]');
      if (productFormSubmit) {
        productFormSubmit.removeEventListener("click", this.productFormSubmitListener);
      }
      this.productFormSubmitListener = null;
    }

    this.initialized = false;
  }

  listenForProductFormSubmit() {
    const productFormSubmit = document.querySelector('.product-form [type="submit"]');
    if (!productFormSubmit) return;

    this.productFormSubmitListener = (event) => {
      // Only prevent submission if gift form validation fails
      if (!this.validateGiftForm()) {
        event.preventDefault();
        this.showValidationErrors();
      } else {
        // Ensure all gift form data is synced before submission
        this.syncAllInputsToProductForm();
      }
    };

    productFormSubmit.addEventListener("click", this.productFormSubmitListener);
  }

  listenForInputChange() {
    this.inputs.forEach((input) => {
      const listener = () => {
        this.syncInputToProductForm(input);
      };

      input.addEventListener("change", listener);
      
      // Store reference for cleanup
      this.inputChangeListeners.push({ input, listener });
    });
  }

  syncInputToProductForm(input) {
    // Find corresponding input in the main product form and update its value
    const productForm = document.querySelector(".product-form");
    if (!productForm) return;

    const correspondingInput = productForm.querySelector(`[name="${input.name}"]`);
    if (correspondingInput) {
      if (input.type === "checkbox" || input.type === "radio") {
        correspondingInput.checked = input.checked;
      } else {
        correspondingInput.value = input.value;
      }
    } else {
      // If corresponding input doesn't exist, create it
      this.createHiddenInput(input);
    }
  }

  validateGiftForm() {
    let isValid = true;
    
    this.inputs.forEach(input => {
      // Clear previous validation state
      input.classList.remove('error');
      
      // Check required fields
      if (input.hasAttribute('required') && !input.value.trim()) {
        input.classList.add('error');
        isValid = false;
      }
      
      // Validate email format
      if (input.type === 'email' && input.value.trim()) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(input.value.trim())) {
          input.classList.add('error');
          isValid = false;
        }
      }
    });
    
    return isValid;
  }

  showValidationErrors() {
    // Find the first error field and focus it
    const firstErrorField = this.querySelector('input.error');
    if (firstErrorField) {
      firstErrorField.focus();
    }
  }

  syncAllInputsToProductForm() {
    this.inputs.forEach(input => {
      this.syncInputToProductForm(input);
    });
  }

  createHiddenInput(sourceInput) {
    const productForm = document.querySelector(".product-form");
    if (!productForm) return;

    // Check if hidden input already exists
    let hiddenInput = productForm.querySelector(`[name="${sourceInput.name}"]`);
    
    if (!hiddenInput) {
      hiddenInput = document.createElement("input");
      hiddenInput.type = "hidden";
      hiddenInput.name = sourceInput.name;
      productForm.appendChild(hiddenInput);
    }

    // Update the value
    if (sourceInput.type === "checkbox" || sourceInput.type === "radio") {
      hiddenInput.value = sourceInput.checked ? sourceInput.value : "";
    } else {
      hiddenInput.value = sourceInput.value;
    }
  }
}

if (!customElements.get("gift-subscription-form")) {
  customElements.define("gift-subscription-form", GiftSubscriptionForm);
}